import { Box, Fade } from '@mui/material'
import { Navigate, useParams } from 'react-router-dom'

import { useAssessment } from '@/shared/hooks'

import LoadingIndicator from './LoadingIndicator'

interface AssessmentGuardProps {
  children: React.ReactNode
}

/**
 * AssessmentGuard component
 * Guards routes that require a processed assessment
 * Shows loading state while checking and redirects to ISFP upload if assessment is not processed
 */
export const AssessmentGuard = ({ children }: AssessmentGuardProps) => {
  const { buildingUuid } = useParams<{ buildingUuid: string }>()
  const { assessment, loading } = useAssessment(buildingUuid)

  // Redirect to ISFP upload page if assessment is not processed
  if (!loading && !assessment?.data?.consumptionBasedAssessment) {
    return <Navigate to={`/${buildingUuid}/isfp-upload`} replace />
  }

  return (
    <Box sx={{ position: 'relative', minHeight: '100vh' }}>
      <Fade
        in={loading}
        timeout={{ enter: 500, exit: 500 }}
        easing={{ enter: 'cubic-bezier(0.4, 0, 0.2, 1)', exit: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
        appear
        mountOnEnter
        unmountOnExit
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LoadingIndicator size={100} />
        </Box>
      </Fade>

      <Fade
        in={!loading && !!assessment}
        timeout={{ enter: 500, exit: 500 }}
        easing={{ enter: 'cubic-bezier(0.4, 0, 0.2, 1)', exit: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
        appear
        mountOnEnter
        unmountOnExit
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            minHeight: '100vh',
          }}
        >
          {children}
        </Box>
      </Fade>
    </Box>
  )
}
